import { useParams, Link } from 'react-router-dom';
import { categories, comics } from '@/mocks/comics';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';

const categoryIcons: Record<string, string> = {
  romance: 'ri-heart-line',
  fantasy: 'ri-magic-line',
  action: 'ri-sword-line',
  mystery: 'ri-search-eye-line',
  bl: 'ri-double-quotes-l',
  school: 'ri-building-line',
  comedy: 'ri-emotion-laugh-line',
  historical: 'ri-ancient-gate-line',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'romance';
  const currentCategory = categories.find((c) => c.slug === currentSlug) || categories[0];
  const filteredComics = comics.filter((c) => c.category === currentSlug);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100/50 py-10 md:py-14">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <nav aria-label="面包屑导航" className="mb-6">
              <ol className="flex items-center gap-1.5 text-xs text-foreground-400 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
                </li>
                <li>
                  <i className="ri-arrow-right-s-line text-[10px]"></i>
                </li>
                <li className="text-foreground-700 font-medium">{currentCategory.name}漫画</li>
              </ol>
            </nav>

            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-500/10">
                <i className={`${categoryIcons[currentSlug] || 'ri-booklet-line'} text-3xl text-primary-500`}></i>
              </div>
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {currentCategory.name}漫画
                </h1>
                <p className="text-sm text-foreground-500 mt-1">{currentCategory.description}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-50 border-b border-background-200/50 sticky top-16 z-30">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center gap-1 overflow-x-auto py-2 scrollbar-none">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    currentSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'text-foreground-600 hover:text-primary-500 hover:bg-primary-50'
                  }`}
                >
                  <i className={`${categoryIcons[cat.slug] || 'ri-booklet-line'} text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <p className="text-sm text-foreground-500">
                共找到 <strong className="text-foreground-950">{filteredComics.length}</strong> 部{currentCategory.name}漫画
              </p>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400">排序：</span>
                <select className="text-sm bg-background-100 border border-background-300/60 rounded-lg px-3 py-1.5 text-foreground-700 outline-none cursor-pointer">
                  <option>最新更新</option>
                  <option>评分最高</option>
                  <option>人气最高</option>
                </select>
              </div>
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {filteredComics.map((comic) => (
                  <ComicCard
                    key={comic.id}
                    id={comic.id}
                    title={comic.title}
                    author={comic.author}
                    cover={comic.cover}
                    category={comic.category}
                    rating={comic.rating}
                    views={comic.views}
                    status={comic.status}
                    latestChapter={`第${comic.chapters[0]?.num}话`}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画内容</p>
                <Link to="/" className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 mt-3 transition-colors whitespace-nowrap cursor-pointer">
                  <i className="ri-arrow-left-line"></i> 返回首页
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}